/**
 * Phase 5 — offline boot.
 *
 * Mounted once (inside NativeAppBoot). Responsibilities:
 *  - register the offline write handlers
 *  - flush the queue on mount and whenever connectivity returns
 *  - show a small offline / pending-writes banner
 *
 * Runs on web too (localStorage-backed), so a flaky browser connection gets the
 * same protection as the native shell.
 */
import { useEffect, useState } from 'react';
import { WifiOff, UploadCloud } from 'lucide-react';
import { isOnline, onConnectivityChange } from '@/lib/native/offline/network';
import { flushWriteQueue, onQueueChange } from '@/lib/native/offline/queue';
import { registerKioskWriteHandler } from '@/lib/native/offline/kioskQueue';

export function NativeOfflineBoot() {
  const [online, setOnline] = useState(true);
  const [pending, setPending] = useState(0);

  useEffect(() => {
    registerKioskWriteHandler();

    let cancelled = false;
    void (async () => {
      const up = await isOnline();
      if (!cancelled) setOnline(up);
      if (up) await flushWriteQueue();
    })();

    const offQueue = onQueueChange((count) => {
      if (!cancelled) setPending(count);
    });

    const offNet = onConnectivityChange((up) => {
      if (cancelled) return;
      setOnline(up);
      if (up) void flushWriteQueue();
    });

    // Retry saved writes every 2 minutes in case a reconnect event was missed.
    const timer = window.setInterval(() => { void flushWriteQueue(); }, 120_000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      offQueue();
      offNet();
    };
  }, []);

  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/welcome/')) return null;
  if (online && pending === 0) return null;

  return (
    <div className="fixed bottom-4 left-1/2 z-[70] -translate-x-1/2 rounded-full border border-border bg-card px-4 py-2 text-xs font-medium text-muted-foreground shadow-lg">
      <span className="flex items-center gap-2">
        {online ? (
          <UploadCloud className="h-3.5 w-3.5" />
        ) : (
          <WifiOff className="h-3.5 w-3.5 text-destructive" />
        )}
        {online
          ? `Syncing ${pending} pending ${pending === 1 ? 'change' : 'changes'}…`
          : pending > 0
            ? `Offline — ${pending} ${pending === 1 ? 'change' : 'changes'} will sync`
            : 'Offline — showing saved data'}
      </span>
    </div>
  );
}

export default NativeOfflineBoot;
